/**
 * The head of every screen: where you are, what this is, and what you can do about it.
 *
 * One component rather than a heading typed into each page, because the parts have to keep
 * the same relationship on every screen - the title names the thing, the actions sit on the
 * right and never wrap under it. Hand-rolled headers drift within a week, and a drifting
 * header is the first thing that makes an application feel like several applications.
 *
 * IT USED TO CARRY A CRUMB TRAIL, and the trail had exactly one user: the call detail
 * PAGE, reading `Calls > Call details`. That page is a dialog now, and a dialog has a title
 * bar rather than a path back - so the trail went with it rather than sitting here as a
 * feature nothing calls.
 *
 * `lede` is a single sentence and it is optional on purpose. It is there for the screens
 * whose title cannot carry its own meaning - "Calls" says nothing, "Every call your agent
 * answered, newest first" says the whole page.
 *
 * IT IS THE STICKY BAR NOW, AND IT USED TO BE ARGUED AGAINST. The settings screen wrapped
 * this in a sticky div of its own and the note above it said, in as many words: solid, not
 * translucent and blurred, because nothing in this product floats, because a blur puts
 * unknown colours behind every ink step, and because `backdrop-filter` creates a containing
 * block under a sticky element. Two of those three have been answered rather than
 * overruled.
 *
 *   THE COLOURS BEHIND IT ARE NOT UNKNOWN. Two things ever scroll under this bar: the page
 *   ground, and white cards. Ink on white is 18.95:1 where the gate checks it at 17.85:1
 *   against the ground, so every step is BETTER behind the blur than the number the gate
 *   pins. There is no third surface for a card to be.
 *
 *   THE CONTAINING BLOCK IS REAL AND IT IS EMPTY. `backdrop-filter` does establish one for
 *   `position: fixed` descendants. Nothing fixed renders in here - the only fixed thing in
 *   the product is `Dialog`, which mounts from the calls list's `<Outlet/>` and not from a
 *   page head. That has to stay true; putting a fixed element inside this bar is the bug.
 *
 * What is left of the old note is the honest part: this is the ONE surface in the product
 * that occludes content, so it is the one allowed to be translucent. It is still not a
 * lift - there is no shadow under it, only the rule along its bottom edge.
 *
 * IT BLEEDS OUT THROUGH `Shell`'S PADDING so its rule reaches both edges of the content
 * column rather than stopping short of them, and cancels that padding's top so nothing
 * shows above it once it is stuck. Negative margins for exactly this are already this
 * codebase's technique - `TABLE_FLUSH`, the calls log, the call dialog's sub-heading. The
 * numbers here have to match `Shell`'s content column; there is no build step that notices
 * if they drift.
 *
 * `z-30` PUTS IT ABOVE THE TOOLTIPS AND BELOW THE DIALOGS. `InfoTip`'s bubble is `z-20`,
 * and a tip on a section heading near the top of the scroll would otherwise paint straight
 * over the pinned header; `Dialog` is `z-50` and has to stay over everything.
 *
 * IT STICKS UNDER THE SHELL'S TOP BAR, NOT AT THE TOP OF THE VIEWPORT. The bar is sticky too,
 * comes first in the DOM, and is `z-40` - above this, because the agent menu drops out
 * of it over this head. `top-(--spacing-bar)` is the bar's own measured height, published by
 * `Shell` the way this publishes its own, so the head lands flush under a bar that wraps to
 * two rows on a narrow window.
 *
 * ANYTHING ELSE THAT STICKS HAS TO CLEAR BOTH, and the numbers are MEASURED rather than
 * written down. The calls log has its own sticky day headings; pinned at `top-0` they sit
 * behind the bar and this head and are never seen. They read `--spacing-bar` plus
 * `--spacing-head`, and this component publishes ONLY its own height as the second: it
 * observes only its own node, so a sum published from here would go stale the moment the
 * bar rewrapped without this head resizing.
 *
 * A CONSTANT WOULD BE WRONG ON HALF THE SCREENS. The bar is 116px on the overview and 138px
 * on the calls log at the same window, because "Every call your agent answered, newest
 * first. Open one to read the conversation beside what the agent made of it." wraps to two
 * lines and "What your agent answered over the last 30 days" does not - and either can wrap
 * differently as the window resizes or the settings tab strip appears. One hardcoded offset
 * is a gap under the bar on one page and a clipped heading on another. The token in
 * `styles.css` is the first-paint fallback, and this overwrites it with the truth.
 */

import { useLayoutEffect, useRef } from "react";
import type { ReactNode } from "react";
import { cx } from "../ui";

/**
 * The content track the overview centres in.
 *
 * Exported because the head and the body under it have to agree: a head centred at 1320
 * over a body that runs to the shell's full width is a page whose title does not sit above
 * its own first column.
 */
export const PAGE_WIDTH = "mx-auto w-full max-w-(--container-page)";

export function PageHead({
  title,
  lede,
  actions,
  meta,
  below,
  contain,
}: {
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
  /** Small facts that belong to the title rather than to the page - a badge, a date. */
  meta?: ReactNode;
  /**
   * A second row inside the sticky bar, under the title - the settings screen's tab strip.
   *
   * THIS REPLACED A `flush` BOOLEAN whose only job was to remove the margin underneath so
   * that a caller could put something directly below the head inside its own sticky
   * wrapper. That was one sticky implementation per screen with the same negative margins
   * written twice. A slot puts the second row INSIDE the one bar, so it pins with the title
   * rather than under it, and the wrapper goes away.
   */
  below?: ReactNode;
  /**
   * How wide the head's contents run, when the page centres its body in a track narrower
   * than the shell. The bar itself is always full bleed - it is the rule along the bottom
   * that has to reach the edges.
   */
  contain?: string;
}) {
  const bar = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const node = bar.current;
    if (!node) return;
    // On the ROOT rather than on a parent, because the element that reads it - a `<th>`
    // deep inside the calls table - is not a descendant of this bar.
    const publish = () =>
      document.documentElement.style.setProperty("--spacing-head", `${node.offsetHeight}px`);
    publish();
    // A resize, a lede that rewraps, the settings tab strip mounting: all three change the
    // height without remounting, and all three are a heading that stops lining up.
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <header
      ref={bar}
      className={cx(
        "sticky top-(--spacing-bar) z-30 mb-5 border-b border-line",
        // 88% rather than opaque: enough to read against, little enough that a card
        // sliding under it is visibly moving rather than disappearing.
        "bg-ground/88 backdrop-blur-[10px]",
        "-mx-8 -mt-6.5 px-8 pt-6.5 pb-[22px]",
        "max-[900px]:-mx-4 max-[900px]:-mt-4.5 max-[900px]:px-4 max-[900px]:pt-4.5",
      )}
    >
      <div className={contain}>
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div className="min-w-0">
            <h1 className="text-3xl font-medium tracking-[-0.03em]">{title}</h1>
            {meta && (
              <div className="mt-[9px] flex flex-wrap items-center gap-2 text-sm text-ink-soft">
                {meta}
              </div>
            )}
            {lede && <p className="mt-1.5 max-w-[68ch] text-base text-ink-soft">{lede}</p>}
          </div>
          {actions && <div className="flex flex-none items-center gap-4">{actions}</div>}
        </div>
        {below && <div className="mt-4.5">{below}</div>}
      </div>
    </header>
  );
}
